import { Link } from 'react-router-dom';
import { useT } from '@/i18n/useT';
import { ROUTES } from '@/lib/config';
import { useBotState, usePauseBot, useResumeBot, useSettlementRefresh } from '@/bot/hooks';
import { BotNav } from './BotNav';
import { AutoModePanel } from './AutoModePanel';
import { BotGate } from './BotGate';

const money = (n: number): string =>
  `${n < 0 ? '-' : n > 0 ? '+' : ''}$${Math.abs(n).toFixed(2)}`;

/**
 * The customer's own dashboard: the same numbers the operator dashboard shows,
 * scoped to this customer's bot. Deliberately the landing screen, because the
 * two questions a customer actually has are "is it running" and "what did it
 * make today".
 */
export function BotDashboardPage() {
  const { t } = useT();
  const state = useBotState();
  const pause = usePauseBot();
  const resume = useResumeBot();
  // Balance and today's P&L land as soon as a trade settles, not on the next
  // tick — Adlain asked for the Dashboard to keep pace with History.
  useSettlementRefresh();

  return (
    <BotGate query={state}>
      {(s) => {
        const d = s.daily;
        const running = s.control.running;
        const cycles = d.cyclesCompleted;
        const winRate = cycles > 0 ? Math.round((d.cycleWins / cycles) * 100) : null;
        const isDemo = s.control.activeAccountType === 'demo';
        const busy = pause.isPending || resume.isPending;

        return (
          <>
            {/* The connection pill lives in the appbar (AppLayout), which is
                where the approved design put it. */}
            {/* Headline card: balance + today's result. */}
            <section className="bot-hero">
              <div className="bot-hero-main">
                <div className="bot-hero-label">
                  {isDemo ? t('dash.balanceDemo') : t('dash.balanceReal')}
                  {/* The approved balance label carried a pulsing live dot. */}
                  <span className="live-dot" />
                </div>
                <div className="bot-hero-value">
                  <span className="cur">$</span>
                  {s.balance ?? '—'}
                </div>
              </div>
              <div className="bot-hero-side">
                <div className="bot-hero-label">{t('dash.todayPnl')}</div>
                <div className={`bot-hero-pnl ${d.netProfitUsd >= 0 ? 'up' : 'down'}`}>
                  {money(d.netProfitUsd)}
                </div>
              </div>
              <div className="bot-hero-stats">
                <div>
                  <strong>{cycles}</strong>
                  <span>{t('dash.cycles')}</span>
                </div>
                <div>
                  <strong>{winRate === null ? '—' : `${winRate}%`}</strong>
                  <span>{t('dash.winRate')}</span>
                </div>
                <div>
                  <strong>
                    <em className="up">{d.cycleWins}</em> · <em className="down">{d.cycleLosses}</em>
                  </strong>
                  <span>{t('dash.cycleWL')}</span>
                </div>
              </div>
            </section>

            {/* Run state. The one control a customer uses daily. */}
            <section className={`bot-card bot-run ${running ? 'on' : 'off'}`}>
              <div>
                <div className="bot-run-state">
                  <i />
                  {/* A safety halt (stop-loss / stop-win / max MG) is NOT a
                      billing state. The approved run card kept RUNNING/PAUSED
                      here and said "Safety halt active" in the hint. */}
                  {s.halted ? t('dash.halted') : running ? t('dash.running') : t('dash.paused')}
                </div>
                <p className="muted">
                  {s.halted
                    ? `${t('dash.haltActive')}${s.haltReason ? ` · ${s.haltReason}` : ''}`
                    : running
                      ? t('dash.acceptingSignals')
                      : (s.control.pauseReason ? (/paused by user/i.test(s.control.pauseReason) ? t('reason.pausedByUser') : s.control.pauseReason) : t('dash.pausedBlurb'))}
                </p>
              </div>
              <button
                type="button"
                className={`bot-toggle ${running ? 'on' : 'off'}`}
                disabled={busy}
                onClick={() => (running ? pause.mutate() : resume.mutate())}
              >
                <span className="knob" />
                {running ? t('dash.stop') : t('dash.start')}
              </button>
            </section>

            {/* ACCOUNT. The approved card led its head with the 32px brand mark
                and put a short "Manage →" on the row itself, not a long
                MANAGE SUBSCRIPTION in the head. */}
            <section className="bot-card">
              <div className="bot-card-head">
                <div className="bot-card-head-brand">
                  <img src="/logo.png" alt="" width={32} height={32} />
                  <h2>{t('dash.account')}</h2>
                </div>
              </div>
              <div className={`bot-account ${isDemo ? 'demo' : 'real'}`}>
                <div>
                  <strong><i className="dot" />{isDemo ? 'DEMO' : 'REAL'}</strong>
                  <span className="muted">{isDemo ? t('dash.demoMode') : t('dash.realMode')}</span>
                </div>
                <Link to={ROUTES.bot} className="bot-link">
                  {t('dash.manage')} →
                </Link>
              </div>
            </section>

            {/* QUICK STATS. The approved card was four rows and the note, not a
                dump of every counter — the rest lives in History, which is what
                the note points at. */}
            <section className="bot-card">
              <div className="bot-card-head">
                <h2>{t('dash.quickStats')}</h2>
                <span className="muted">{t('dash.today')}</span>
              </div>
              <dl className="bot-stats">
                <div><dt>{t('dash.cyclesToday')}</dt><dd>{cycles}</dd></div>
                <div><dt>{t('dash.winRate')}</dt><dd>{winRate === null ? '—' : `${winRate}%`}</dd></div>
                <div><dt>{t('dash.netPnl')}</dt><dd className={d.netProfitUsd >= 0 ? 'up' : 'down'}>{money(d.netProfitUsd)}</dd></div>
                <div><dt>{t('dash.lastMgStep')}</dt><dd>{s.step}</dd></div>
              </dl>
              <p className="bot-note">{t('dash.todayNote')}</p>
            </section>

            {/* Automatic Mode's live analysis. Renders its own "not enabled"
                state, so a bot without the mode is unaffected. */}
            <AutoModePanel />

            <BotNav />
          </>
        );
      }}
    </BotGate>
  );
}
